// DeckListPage: Presentational page for listing decks, on the ui/ master library.
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Copy, ArrowUpCircle, Link as LinkIcon, Unlink, Trash2, Layers, RotateCw } from 'lucide-react';
import Page from '../ui/Page';
import PageStack from '../ui/PageStack';
import ActionBar from '../ui/ActionBar';
import FilterStrip from '../ui/FilterStrip';
import SectionHeader from '../ui/SectionHeader';
import TileGrid from '../ui/TileGrid';
import DeckTile from '../ui/DeckTile';
import ContextMenu from '../ui/ContextMenu';
import ConfirmDialog from '../ui/ConfirmDialog';
import EmptyState from '../ui/EmptyState';
import Skeleton from '../ui/Skeleton';
import { useToast } from '../ui/Toast';
import { apiClient } from '../api/apiClient';
import NewDeckModal from './NewDeckModal';
import { toSource } from '../ui/DeckPicker';

export default function DeckListPage(props) {
  const {
    decks, showNewDeckModal, setShowNewDeckModal,
    loading, error,
    confirmDeleteDeck, showDeleteConfirmModal, deckToDelete, handleDeleteDeck, handleCancelDeleteDeck,
    handleCopyDeckToVirtual, isOwnProfile = true, onRefresh,
  } = props;

  const navigate = useNavigate();
  const toast = useToast();

  const [copyTarget, setCopyTarget] = useState(null);
  const [upgradeTarget, setUpgradeTarget] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('lastEdited'); // 'lastEdited' | 'name'
  const [sortDir, setSortDir] = useState('desc');      // 'asc' | 'desc'

  const sortDecks = (list) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortBy === 'name') return dir * (a.name || '').localeCompare(b.name || '', 'de');
      const av = a.lastEdited ? new Date(a.lastEdited).getTime() : null;
      const bv = b.lastEdited ? new Date(b.lastEdited).getTime() : null;
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return dir * (av - bv);
    });
  };
  const matches = (d) => d.name?.toLowerCase().includes(search.trim().toLowerCase());

  const physicalDecks = useMemo(() => decks.filter((d) => toSource(d) === 'physical'), [decks]);
  const virtualDecks = useMemo(() => decks.filter((d) => toSource(d) === 'virtual' || toSource(d) === ''), [decks]);
  const visibleVirtual = sortDecks(virtualDecks.filter(matches));
  const visiblePhysical = sortDecks(physicalDecks.filter(matches));

  const handleCopyConfirm = async () => {
    if (!copyTarget) return;
    await handleCopyDeckToVirtual(copyTarget.id);
    toast.push({ tone: 'success', message: 'Deck kopiert.' });
    setCopyTarget(null);
  };
  const handleUpgradeConfirm = async () => {
    if (!upgradeTarget) return;
    try {
      await apiClient.post(`/api/decks/${upgradeTarget.id}/upgrade`);
      toast.push({ tone: 'success', message: 'Deck upgegradet.' });
      onRefresh?.();
    } catch (e) {
      toast.push({ tone: 'error', message: 'Upgrade fehlgeschlagen.' });
    }
    setUpgradeTarget(null);
  };
  const handleLinkChange = async (deck, sourceBinderId) => {
    try {
      await apiClient.patch(`/api/decks/${deck.id}/source-binder`, { sourceBinderId });
      onRefresh?.();
    } catch (e) {
      toast.push({ tone: 'error', message: 'Verknüpfung fehlgeschlagen.' });
    }
  };
  const onDeleteConfirmed = async () => {
    if (!deckToDelete) return;
    await handleDeleteDeck(deckToDelete.id);
    toast.push({ tone: 'success', message: 'Deck gelöscht.' });
  };

  const menuFor = (deck) => {
    if (!isOwnProfile) {
      return (
        <ContextMenu ariaLabel={`Aktionen für ${deck.name}`}>
          <ContextMenu.Item icon={Copy} onSelect={() => setCopyTarget(deck)}>Zu meinen Decks hinzufügen</ContextMenu.Item>
        </ContextMenu>
      );
    }
    const isPhysical = toSource(deck) === 'physical';
    const hasLink = !isPhysical && !!deck.sourceBinderId;
    return (
      <ContextMenu ariaLabel={`Aktionen für ${deck.name}`}>
        <ContextMenu.Item icon={Copy} onSelect={() => setCopyTarget(deck)}>Kopieren</ContextMenu.Item>
        {isPhysical && <ContextMenu.Item icon={ArrowUpCircle} onSelect={() => setUpgradeTarget(deck)}>Upgraden</ContextMenu.Item>}
        {!isPhysical && hasLink && <ContextMenu.Item icon={Unlink} onSelect={() => handleLinkChange(deck, null)}>Verknüpfung lösen</ContextMenu.Item>}
        {!isPhysical && !hasLink && physicalDecks.length > 0 && (
          <ContextMenu.Submenu icon={LinkIcon} label="Verknüpfen">
            {physicalDecks.map((p) => (
              <ContextMenu.Item key={p.id} onSelect={() => handleLinkChange(deck, p.origid)}>{p.name || 'Unbenanntes Deck'}</ContextMenu.Item>
            ))}
          </ContextMenu.Submenu>
        )}
        {!isPhysical && (
          <>
            <ContextMenu.Separator />
            <ContextMenu.Item icon={Trash2} danger onSelect={() => confirmDeleteDeck(deck)}>Löschen</ContextMenu.Item>
          </>
        )}
      </ContextMenu>
    );
  };

  const tile = (deck) => (
    <DeckTile
      key={deck.id}
      image={deck.commanderImageUrl}
      artist={deck.commanderArtist}
      name={deck.name || 'Unbenanntes Deck'}
      count={deck.cardCount}
      link={deck.sourceBinderName}
      href={`/decks/${deck.id}/editor`}
      onClick={() => navigate(`/decks/${deck.id}/editor`)}
      action={menuFor(deck)}
    />
  );

  const sectionBody = (list, full) => {
    if (loading) return <TileGrid>{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} width="100%" height={210} radius="lg" />)}</TileGrid>;
    if (list.length === 0) {
      if (search) return <EmptyState variant="filter" size="compact" icon={Layers} title="Keine Treffer" />;
      return <EmptyState size="compact" icon={Layers} title={full.length === 0 ? 'Keine Decks vorhanden' : 'Keine Treffer'} />;
    }
    return (
      <TileGrid collapsedRows={2} showMoreLabel={(h, t) => `Alle ${t} anzeigen`}>
        {list.map(tile)}
      </TileGrid>
    );
  };

  if (error && !loading) {
    return (
      <Page>
        <EmptyState
          variant="error"
          icon={RotateCw}
          onRetry={() => onRefresh?.()}
          title="Decks konnten nicht geladen werden"
          description={error}
        />
      </Page>
    );
  }

  return (
    <Page>
      <ActionBar
        left={<ActionBar.Stat id="decks" value={decks.length} label="Decks" />}
        right={isOwnProfile ? (
          <ActionBar.Action
            icon={Plus}
            label="Neues Deck"
            variant="primary"
            onClick={() => setShowNewDeckModal(true)}
          />
        ) : null}
      />

      <FilterStrip>
        <FilterStrip.Search value={search} onChange={setSearch} placeholder="Suche nach Deck-Name…" />
        <FilterStrip.RightGroup>
          <FilterStrip.Item id="sortieren">
            <FilterStrip.Group label="Sortieren">
              <FilterStrip.PillMenu
                mode="single"
                options={[
                  { value: 'lastEdited', label: 'Zuletzt bearbeitet' },
                  { value: 'name', label: 'Name' },
                ]}
                value={sortBy}
                onChange={setSortBy}
              />
              <FilterStrip.SortDirection direction={sortDir} onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} />
            </FilterStrip.Group>
          </FilterStrip.Item>
        </FilterStrip.RightGroup>
      </FilterStrip>

      <PageStack>
        {(virtualDecks.length > 0 || (isOwnProfile && decks.length > 0)) && (
          <section>
            <SectionHeader>Virtuelle Decks</SectionHeader>
            {sectionBody(visibleVirtual, virtualDecks)}
          </section>
        )}
        {physicalDecks.length > 0 && (
          <section>
            <SectionHeader>Physische Decks</SectionHeader>
            {sectionBody(visiblePhysical, physicalDecks)}
          </section>
        )}
        {!loading && decks.length === 0 && (
          <EmptyState
            icon={Layers}
            title={isOwnProfile ? 'Noch keine Decks' : 'Dieser User hat noch keine Decks.'}
            description={isOwnProfile ? 'Erstelle dein erstes Deck.' : undefined}
            action={isOwnProfile ? <button type="button" className="btn-primary" onClick={() => setShowNewDeckModal(true)}><Plus size={16} aria-hidden /> Neues Deck</button> : undefined}
          />
        )}
      </PageStack>

      <NewDeckModal
        open={showNewDeckModal}
        onOpenChange={(o) => { if (!o && props.bulkStep === 'importing') return; setShowNewDeckModal(o); if (!o) { props.setActiveTab('create'); props.handleCloseBulkModal(); props.setImportError(null); } }}
        {...props}
      />

      <ConfirmDialog
        open={showDeleteConfirmModal && !!deckToDelete}
        onOpenChange={(o) => { if (!o) handleCancelDeleteDeck(); }}
        title="Deck löschen?"
        description={deckToDelete ? `„${deckToDelete.name}" wird unwiderruflich gelöscht.` : undefined}
        confirmLabel="Löschen"
        tone="danger"
        onConfirm={onDeleteConfirmed}
      />
      <ConfirmDialog
        open={!!copyTarget}
        onOpenChange={(o) => { if (!o) setCopyTarget(null); }}
        title={isOwnProfile ? 'Deck kopieren?' : 'Zu meinen Decks hinzufügen?'}
        description={copyTarget ? `„${copyTarget.name}" wird als virtuelles Deck ${isOwnProfile ? 'kopiert' : 'zu deinen Decks hinzugefügt'}.` : undefined}
        confirmLabel={isOwnProfile ? 'Kopieren' : 'Hinzufügen'}
        onConfirm={handleCopyConfirm}
      />
      <ConfirmDialog
        open={!!upgradeTarget}
        onOpenChange={(o) => { if (!o) setUpgradeTarget(null); }}
        title="Deck upgraden?"
        description={upgradeTarget ? `„${upgradeTarget.name}" wird als neues virtuelles Deck angelegt — physisches Deck bleibt unverändert.` : undefined}
        confirmLabel="Upgraden"
        onConfirm={handleUpgradeConfirm}
      />
    </Page>
  );
}
